import { useState, useCallback } from "react";
import { Button, Slideover } from "@xometry/xometry_loft";
import { useMutation, BaseMutationOptions } from "@apollo/client";

import {
  AddMarketplaceBuyerAddressQuery,
  AddMarketplaceBuyerAddressMutationVariables,
  AddMarketplaceBuyerAddressMutationResult,
  MarketplaceAddressInput,
} from "../../../schema/generated/schema";

import ProposalJsonForm from "../../JsonSchemaLoftTheme";
import { elements, elementsArrangement, schema } from "./formConfig";

import {
  LineItemFormContainer as LineItemFormContainerShared,
  SlideOverContainer,
  SlideOverStyling,
} from "../Common/SlideOver/SlideOver.style";

import {
  CloseButtonContainer,
  LineItemFormContainer,
} from "./CustomerContactInformation.styles";

type AddMarketplaceBuyerAddressMutationOptions = BaseMutationOptions<
  AddMarketplaceBuyerAddressMutationResult,
  AddMarketplaceBuyerAddressMutationVariables
>;

type CustomerContactInformationProps = {
  open: boolean;
  // callback executed once the user clicks on (x) icon or cancel button
  onCancel: () => void;
  // flag forwarded to ProposalJsonForm
  isSeller: boolean;
  // flag forwarded to ProposalJsonForm
  canEdit: boolean;
  // which user is going to have a new shipping address
  userId: string;
  // callback executed once the user finish the addition of the new address
  onSubmit?: (props: Partial<MarketplaceAddressInput>) => void;
} & AddMarketplaceBuyerAddressMutationOptions;

const INITIAL_FORM_VALUE = {
  country: "US",
  setDefaultAddress: false,
};

const CustomerContactInformation = ({
  open,
  onCancel,
  isSeller,
  canEdit,
  userId,
  onSubmit,
  ...mutationOptions
}: CustomerContactInformationProps) => {
  const [formData, setFormData] =
    useState<Partial<MarketplaceAddressInput>>(INITIAL_FORM_VALUE);

  const handleChange = useCallback(event => {
    setFormData(event.formData);
  }, []) as any;

  const [addMarketplaceBuyerAddress] = useMutation<
    AddMarketplaceBuyerAddressMutationResult,
    AddMarketplaceBuyerAddressMutationVariables
  >(AddMarketplaceBuyerAddressQuery, {
    ...mutationOptions,

    onCompleted: () => {
      setFormData({});
      onSubmit?.(formData);
    },
  });

  const handleSubmit = event => {
    addMarketplaceBuyerAddress({
      variables: {
        userId,
        address: { ...event.formData, setDefaultAddress: undefined }, // removing setDefaultAddress due to not being part of the address itself
        setDefaultAddress: true,
      },
    });
  };

  if (!open) {
    return null;
  }

  return (
    <div css={SlideOverContainer}>
      <Slideover
        css={SlideOverStyling}
        isOpen
        onClose={onCancel}
        origin="right"
        slideoverId="line-item-config"
        className="slideover-panel"
      >
        <div css={CloseButtonContainer}>
          <i
            aria-label="Close"
            className="fal fa-times"
            onClick={onCancel}
            onKeyDown={onCancel}
            role="button"
            tabIndex={0}
            title="Close"
          />
        </div>

        <div css={[LineItemFormContainerShared, LineItemFormContainer]}>
          {userId ? (
            <ProposalJsonForm
              canEdit={canEdit}
              elementArrangement={elementsArrangement}
              elements={elements}
              formData={formData}
              id="customer-contact-information"
              isSeller={isSeller}
              onChange={handleChange}
              onSubmit={handleSubmit}
              schema={schema}
            >
              <div className="buttons-container">
                <Button
                  type="reset"
                  kind="text"
                  scale="large"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!canEdit}
                  form="customer-contact-information"
                  kind="primary"
                  scale="large"
                  type="submit"
                >
                  Save
                </Button>
              </div>
            </ProposalJsonForm>
          ) : (
            <p>
              Please select a buyer for this proposal before managing shipping
              addresses.
            </p>
          )}
        </div>
      </Slideover>
    </div>
  );
};

CustomerContactInformation.displayName = "CustomerContactInformation";
CustomerContactInformation.defaultProps = {
  onSubmit: undefined,
};

export default CustomerContactInformation;
